import api from '../api/client';

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const keyToBase64 = (key: ArrayBuffer | null) => {
  if (!key) return '';
  const bytes = new Uint8Array(key);
  let str = '';
  bytes.forEach((b) => {
    str += String.fromCharCode(b);
  });
  return btoa(str);
};

export async function registerPushSubscription(deviceName?: string, forceResubscribe: boolean = false): Promise<boolean> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] Browser does not support service workers or push.');
      return false;
    }

    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      console.warn('[Push] VAPID public key not configured.');
      return false;
    }

    // Permission
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
    } else if (Notification.permission !== 'granted') {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    // If forcing re-subscription, unsubscribe first
    if (forceResubscribe && subscription) {
      console.info('[Push] Force re-subscribing, unsubscribing old subscription...');
      await subscription.unsubscribe();
      subscription = null;
    }

    // Check if subscription exists but might be stale (different VAPID key)
    if (subscription) {
      try {
        // Try to get the keys - if this fails, subscription is invalid
        const p256dh = subscription.getKey('p256dh');
        const auth = subscription.getKey('auth');
        if (!p256dh || !auth) {
          console.info('[Push] Subscription keys missing, re-subscribing...');
          await subscription.unsubscribe();
          subscription = null;
        }
      } catch (err) {
        console.info('[Push] Subscription invalid, re-subscribing...', err);
        // Store reference before setting to null (non-null assertion safe here since we're in if(subscription) block)
        const oldSub = subscription!;
        subscription = null;
        try {
          await oldSub.unsubscribe();
        } catch {
          // Ignore unsubscribe errors
        }
      }
    }

    if (!subscription) {
      console.info('[Push] Creating new push subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    const p256dh = keyToBase64(subscription.getKey('p256dh'));
    const auth = keyToBase64(subscription.getKey('auth'));

    // Always send to backend - the backend will handle deduplication
    // This ensures the subscription is always up-to-date on the server
    await api.post('/notifications/subscribe', {
      endpoint: subscription.endpoint,
      p256dh_key: p256dh,
      auth_key: auth,
      device_name: deviceName || navigator.userAgent,
    });

    console.info('[Push] Subscription registered/verified.');
    return true;
  } catch (err) {
    console.error('[Push] Failed to register', err);
    return false;
  }
}

